import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import usePageTitle from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  FileSpreadsheet, FileText, Printer, Filter, ChevronDown, List, 
  ArrowLeft, Download, Loader2, BarChart3, X, Minus, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { DMYDateInput } from "@/components/ui/dmy-date-picker";

import { formatDMY } from "@/lib/dmy";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const LedgerPreviewPage = () => {
  usePageTitle("Ledger Preview");
  const [searchParams] = useSearchParams();
  
  // Get params from URL
  const clientCode = searchParams.get('client') || '';
  const initialViewMode = searchParams.get('mode') || 'parcel';
  const dateFrom = searchParams.get('from') || '';
  const dateTo = searchParams.get('to') || '';
  
  // State
  const [loading, setLoading] = useState(true);
  const [accountViewData, setAccountViewData] = useState(null);
  const [accountViewMode, setAccountViewMode] = useState(initialViewMode);
  const [showFullDetails, setShowFullDetails] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [exportColumns, setExportColumns] = useState({
    sno: true, date: true, tracking: true, sale: true, gp: true,
    coll: true, debit: true, credit: true, balance: true, grp_bal: true
  });
  const [exporting, setExporting] = useState(false);
  
  // Report generation state
  const [showAccountReportDialog, setShowAccountReportDialog] = useState(false);
  const [selectedAccountReports, setSelectedAccountReports] = useState([]);
  const [accountReportData, setAccountReportData] = useState(null);
  const [accountReportLoading, setAccountReportLoading] = useState(false);
  const [showFullScreenReport, setShowFullScreenReport] = useState(false);

  // Manual entry state
  const [showManualEntryDialog, setShowManualEntryDialog] = useState(false);
  const [manualEntryType, setManualEntryType] = useState('debit'); // 'debit' or 'credit'
  const [manualEntryForm, setManualEntryForm] = useState({
    amount: '',
    description: '',
    date: '',
    category: 'adjustment'
  });
  const [manualEntryLoading, setManualEntryLoading] = useState(false);

  // Report types configuration
  const accountReportTypes = [
    { id: 'outstanding_balance', name: 'Outstanding Balance Report', description: 'Partners with high outstanding balances', color: 'red' },
    { id: 'negative_gp', name: 'Negative GP Report', description: 'Loss-making transactions', color: 'red' },
    { id: 'no_invoice', name: 'No Invoice Report', description: 'Parcels without invoice amount', color: 'orange' },
    { id: 'high_value', name: 'High Value Transactions (≥₹10K)', description: 'Large transactions for audit', color: 'blue' },
    { id: 'company_payments', name: 'Company Payments Report', description: 'ESR/Bank payments received', color: 'green' },
    { id: 'personal_collections', name: 'Personal Collections Report', description: 'Neutral payments (no balance effect)', color: 'yellow' },
    { id: 'multi_parcel', name: 'Multi-Parcel Payments', description: 'Payments covering multiple parcels', color: 'purple' },
    { id: 'pending_payment', name: 'Pending Payment Report', description: 'Parcels awaiting payment', color: 'orange' },
    { id: 'profit_summary', name: 'Profit Share Summary', description: 'GP distribution by partner', color: 'emerald' },
    { id: 'dormant_account', name: 'Dormant Account Report', description: 'No transactions in last 30 days', color: 'slate' },
    { id: 'dispute_adjustment', name: 'Dispute/Adjustment Report', description: 'Parcels with price adjustments', color: 'rose' },
    { id: 'settlement_pending', name: 'Settlement Pending Report', description: 'Unsettled profit share with 3rd party', color: 'indigo' },
    { id: 'month_end_summary', name: 'Month-End Summary', description: 'Comprehensive monthly balance summary', color: 'cyan' },
  ];

  // Fetch data on mount
  useEffect(() => {
    if (clientCode) {
      fetchAccountData();
    } else {
      setLoading(false);
    }
  }, [clientCode, accountViewMode]);

  const fetchAccountData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/partner-ledger-account/${clientCode}?view_mode=${accountViewMode}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccountViewData(data);
      } else {
        toast.error('Failed to load account data');
      }
    } catch (error) {
      console.error('Error fetching account data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const selectedCols = Object.entries(exportColumns)
        .filter(([k, v]) => v)
        .map(([k]) => k)
        .join(',');
      
      let url = `${API_URL}/api/partner-ledger-account/${clientCode}/export?format=${format}&view_mode=${accountViewMode}&columns=${selectedCols}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      if (showFullDetails) url += `&full_details=true`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const filename = `PLA_${clientCode}_${accountViewMode}${showFullDetails ? '_FullDetails' : ''}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        a.remove();
        toast.success(`${format.toUpperCase()} exported successfully`);
      } else {
        toast.error(`Failed to export ${format.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Toggle report selection
  const toggleAccountReportSelection = (reportId) => {
    setSelectedAccountReports(prev => {
      if (prev.includes(reportId)) {
        return prev.filter(id => id !== reportId);
      } else {
        return [...prev, reportId];
      }
    });
  };

  // Get report count for a type
  const getAccountReportCount = (reportId) => {
    if (!accountViewData) return 0;
    const data = accountViewMode === "payment" ? accountViewData.payment_groups : accountViewData.ledger_entries;
    if (!data) return 0;
    
    switch(reportId) {
      case 'negative_gp':
        if (accountViewMode === "payment") {
          let count = 0;
          data.forEach(g => g.parcels?.forEach(p => { if (p.has_negative_gp) count++; }));
          return count;
        }
        return data.filter(p => p.has_negative_gp).length;
      case 'no_invoice':
        if (accountViewMode === "payment") {
          let count = 0;
          data.forEach(g => g.parcels?.forEach(p => { if (p.has_no_invoice) count++; }));
          return count;
        }
        return data.filter(p => p.has_no_invoice).length;
      case 'high_value':
        if (accountViewMode === "payment") {
          return data.filter(g => g.payment_amount >= 10000).length;
        }
        return data.filter(p => (p.sale_amount || 0) >= 10000).length;
      case 'company_payments':
        if (accountViewMode === "payment") {
          return data.filter(g => g.is_company_payment).length;
        }
        return data.filter(p => p.entries?.some(e => e.category === 'esr_payment')).length;
      case 'personal_collections':
        if (accountViewMode === "payment") {
          return data.filter(g => !g.is_company_payment && g.payment_amount > 0).length;
        }
        return data.filter(p => p.entries?.some(e => e.category === 'personal')).length;
      case 'multi_parcel':
        return accountViewMode === "payment" ? data.filter(g => g.parcel_count > 1).length : 0;
      case 'pending_payment':
        if (accountViewMode === "payment") {
          return data.filter(g => g.payment_amount === 0).length;
        }
        return data.filter(p => !p.entries?.some(e => e.category === 'esr_payment' || e.category === 'personal')).length;
      case 'dormant_account':
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        let latestDate = null;
        if (accountViewMode === "payment") {
          data.forEach(g => g.parcels?.forEach(p => {
            const d = p.date ? new Date(p.date) : null;
            if (d && (!latestDate || d > latestDate)) latestDate = d;
          }));
        } else {
          data.forEach(p => {
            const d = p.date ? new Date(p.date) : null;
            if (d && (!latestDate || d > latestDate)) latestDate = d;
          });
        }
        return (!latestDate || latestDate < thirtyDaysAgo) ? 1 : 0;
      case 'dispute_adjustment':
        if (accountViewMode === "payment") {
          let count = 0;
          data.forEach(g => g.parcels?.forEach(p => {
            const saleAmt = p.sale_amount || 0;
            const costAmt = p.cost_amount || 0;
            const pctDiff = costAmt > 0 ? (Math.abs(saleAmt - costAmt) / costAmt) * 100 : 0;
            if (p.has_negative_gp || pctDiff > 50) count++;
          }));
          return count;
        }
        return data.filter(p => {
          const saleAmt = p.sale_amount || 0;
          const costAmt = p.cost_amount || 0;
          const pctDiff = costAmt > 0 ? (Math.abs(saleAmt - costAmt) / costAmt) * 100 : 0;
          return p.has_negative_gp || pctDiff > 50;
        }).length;
      case 'settlement_pending':
        const hasThird = accountViewData?.profit_sharing?.third_percentage > 0;
        const thirdReferrals = accountViewData?.third_partner_referrals?.length || 0;
        if (!hasThird && thirdReferrals === 0) return 0;
        
        let pendingCount = thirdReferrals;
        if (hasThird) {
          if (accountViewMode === "payment") {
            data.forEach(g => g.parcels?.forEach(p => {
              if (p.third_party_share > 0) pendingCount++;
            }));
          } else {
            pendingCount += data.filter(p => p.third_party_share > 0).length;
          }
        }
        return pendingCount;
      case 'month_end_summary':
        return 1;
      default:
        return 0;
    }
  };

  // Generate Account Reports
  const generateAccountReports = async () => {
    if (selectedAccountReports.length === 0) {
      toast.error("Please select at least one report type");
      return;
    }
    
    if (!accountViewData) {
      toast.error("Please load account data first");
      return;
    }
    
    setAccountReportLoading(true);
    
    try {
      const reports = {};
      const data = accountViewMode === "payment" ? accountViewData.payment_groups : accountViewData.ledger_entries;
      
      selectedAccountReports.forEach(reportId => {
        switch(reportId) {
          case 'outstanding_balance':
            reports.outstanding_balance = {
              title: 'Outstanding Balance Report',
              balance: accountViewData.closing_balance,
              status: accountViewData.closing_balance > 0 ? 'Partner owes you' : accountViewData.closing_balance < 0 ? 'You owe partner' : 'Settled',
              total_debit: accountViewData.totals?.total_debit,
              total_credit: accountViewData.totals?.total_credit
            };
            break;
            
          case 'negative_gp':
            if (accountViewMode === "payment") {
              const negGpParcels = [];
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  if (p.has_negative_gp) negGpParcels.push(p);
                });
              });
              reports.negative_gp = { title: 'Negative GP Report', items: negGpParcels, count: negGpParcels.length };
            } else {
              const negGp = data?.filter(p => p.has_negative_gp) || [];
              reports.negative_gp = { title: 'Negative GP Report', items: negGp, count: negGp.length };
            }
            break;
            
          case 'no_invoice':
            if (accountViewMode === "payment") {
              const noInvParcels = [];
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  if (p.has_no_invoice) noInvParcels.push(p);
                });
              });
              reports.no_invoice = { title: 'No Invoice Report', items: noInvParcels, count: noInvParcels.length };
            } else {
              const noInv = data?.filter(p => p.has_no_invoice) || [];
              reports.no_invoice = { title: 'No Invoice Report', items: noInv, count: noInv.length };
            }
            break;
            
          case 'high_value':
            if (accountViewMode === "payment") {
              const highVal = data?.filter(g => g.payment_amount >= 10000) || [];
              reports.high_value = { title: 'High Value Transactions (≥₹10K)', items: highVal, count: highVal.length };
            } else {
              const highVal = data?.filter(p => (p.sale_amount || 0) >= 10000) || [];
              reports.high_value = { title: 'High Value Transactions (≥₹10K)', items: highVal, count: highVal.length };
            }
            break;
            
          case 'company_payments':
            if (accountViewMode === "payment") {
              const companyPay = data?.filter(g => g.is_company_payment) || [];
              const totalCompany = companyPay.reduce((sum, g) => sum + (g.payment_amount || 0), 0);
              reports.company_payments = { title: 'Company Payments Report', items: companyPay, count: companyPay.length, total: totalCompany };
            } else {
              const companyPay = data?.filter(p => p.entries?.some(e => e.category === 'esr_payment')) || [];
              reports.company_payments = { title: 'Company Payments Report', items: companyPay, count: companyPay.length };
            }
            break;
            
          case 'personal_collections':
            if (accountViewMode === "payment") {
              const personal = data?.filter(g => !g.is_company_payment && g.payment_amount > 0) || [];
              const totalPersonal = personal.reduce((sum, g) => sum + (g.payment_amount || 0), 0);
              reports.personal_collections = { title: 'Personal Collections Report', items: personal, count: personal.length, total: totalPersonal };
            } else {
              const personal = data?.filter(p => p.entries?.some(e => e.category === 'personal')) || [];
              reports.personal_collections = { title: 'Personal Collections Report', items: personal, count: personal.length };
            }
            break;
            
          case 'multi_parcel':
            if (accountViewMode === "payment") {
              const multiParcel = data?.filter(g => g.parcel_count > 1) || [];
              reports.multi_parcel = { title: 'Multi-Parcel Payments', items: multiParcel, count: multiParcel.length };
            } else {
              reports.multi_parcel = { title: 'Multi-Parcel Payments', items: [], count: 0, note: 'Switch to Payment-wise view for this report' };
            }
            break;
            
          case 'pending_payment':
            if (accountViewMode === "payment") {
              // In payment-wise view, extract parcels from groups with no payment
              const pendingGroups = data?.filter(g => g.payment_amount === 0) || [];
              const pendingParcels = [];
              let totalPendingAmount = 0;
              
              pendingGroups.forEach(group => {
                if (group.parcels && group.parcels.length > 0) {
                  group.parcels.forEach(parcel => {
                    pendingParcels.push({
                      tracking: parcel.tracking,
                      customer: parcel.customer,
                      sale_amount: parcel.sale_amount || 0,
                      date: parcel.date,
                    });
                    totalPendingAmount += parcel.sale_amount || 0;
                  });
                }
              });
              
              reports.pending_payment = { 
                title: 'Pending Payment Report', 
                items: pendingParcels, 
                count: pendingParcels.length,
                total_pending: totalPendingAmount
              };
            } else {
              const pending = data?.filter(p => !p.entries?.some(e => e.category === 'esr_payment' || e.category === 'personal')) || [];
              const totalPendingAmount = pending.reduce((sum, p) => sum + (p.sale_amount || 0), 0);
              reports.pending_payment = { 
                title: 'Pending Payment Report', 
                items: pending, 
                count: pending.length,
                total_pending: totalPendingAmount
              };
            }
            break;
            
          case 'profit_summary':
            let totalGpSelf = 0, totalGpPartner = 0;
            if (accountViewMode === "payment") {
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  const gpSelfEntry = p.entries?.find(e => e.category === 'gp_self');
                  if (gpSelfEntry) totalGpSelf += gpSelfEntry.debit || 0;
                  totalGpPartner += p.gp_to_partner || 0;
                });
              });
            } else {
              data?.forEach(p => {
                const gpSelfEntry = p.entries?.find(e => e.category === 'gp_self');
                if (gpSelfEntry) totalGpSelf += gpSelfEntry.debit || 0;
                totalGpPartner += p.gp_to_partner || 0;
              });
            }
            reports.profit_summary = {
              title: 'Profit Share Summary',
              self_share: totalGpSelf,
              partner_share: totalGpPartner,
              total_gp: totalGpSelf + totalGpPartner
            };
            break;
            
          case 'dormant_account':
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            let latestTxnDate = null;
            let dormantParcels = [];
            
            // Helper function to parse DDMMYY format dates
            const parseDDMMYY = (dateStr) => {
              if (!dateStr) return null;
              const str = String(dateStr).trim();
              
              // Handle DDMMYY format (e.g., "150225")
              if (str.length === 6 && /^\d{6}$/.test(str)) {
                const day = parseInt(str.substring(0, 2));
                const month = parseInt(str.substring(2, 4)) - 1; // JS months are 0-indexed
                const year = 2000 + parseInt(str.substring(4, 6)); // Assume 20xx
                return new Date(year, month, day);
              }
              
              // Fallback to standard Date parsing for other formats
              const parsed = new Date(str);
              return isNaN(parsed.getTime()) ? null : parsed;
            };
            
            if (accountViewMode === "payment") {
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  const txnDate = parseDDMMYY(p.date);
                  if (txnDate && (!latestTxnDate || txnDate > latestTxnDate)) {
                    latestTxnDate = txnDate;
                  }
                  if (txnDate && txnDate < thirtyDaysAgo) {
                    dormantParcels.push(p);
                  }
                });
              });
            } else {
              data?.forEach(p => {
                const txnDate = parseDDMMYY(p.date);
                if (txnDate && (!latestTxnDate || txnDate > latestTxnDate)) {
                  latestTxnDate = txnDate;
                }
                if (txnDate && txnDate < thirtyDaysAgo) {
                  dormantParcels.push(p);
                }
              });
            }
            
            const daysSinceLastTxn = latestTxnDate ? Math.floor((new Date() - latestTxnDate) / (1000 * 60 * 60 * 24)) : null;
            const isDormant = daysSinceLastTxn === null || daysSinceLastTxn > 30;
            
            reports.dormant_account = {
              title: 'Dormant Account Report',
              is_dormant: isDormant,
              days_since_last_transaction: daysSinceLastTxn,
              last_transaction_date: latestTxnDate?.toLocaleDateString() || 'N/A',
              old_parcels: dormantParcels,
              old_parcels_count: dormantParcels.length,
              status: isDormant ? 'Dormant - No recent activity' : 'Active'
            };
            break;
            
          case 'dispute_adjustment':
            let adjustedParcels = [];
            
            if (accountViewMode === "payment") {
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  const saleAmt = p.sale_amount || 0;
                  const costAmt = p.cost_amount || 0;
                  const diff = Math.abs(saleAmt - costAmt);
                  const pctDiff = costAmt > 0 ? (diff / costAmt) * 100 : 0;
                  if (p.has_negative_gp || pctDiff > 50) {
                    adjustedParcels.push({
                      ...p,
                      adjustment_amount: saleAmt - costAmt,
                      adjustment_pct: pctDiff.toFixed(1)
                    });
                  }
                });
              });
            } else {
              data?.forEach(p => {
                const saleAmt = p.sale_amount || 0;
                const costAmt = p.cost_amount || 0;
                const diff = Math.abs(saleAmt - costAmt);
                const pctDiff = costAmt > 0 ? (diff / costAmt) * 100 : 0;
                if (p.has_negative_gp || pctDiff > 50) {
                  adjustedParcels.push({
                    ...p,
                    adjustment_amount: saleAmt - costAmt,
                    adjustment_pct: pctDiff.toFixed(1)
                  });
                }
              });
            }
            
            reports.dispute_adjustment = {
              title: 'Dispute/Adjustment Report',
              items: adjustedParcels,
              count: adjustedParcels.length,
              total_adjustment: adjustedParcels.reduce((sum, p) => sum + (p.adjustment_amount || 0), 0)
            };
            break;
            
          case 'settlement_pending':
            let pendingSettlements = [];
            const hasThirdParty = accountViewData?.profit_sharing?.third_percentage > 0;
            
            if (hasThirdParty) {
              if (accountViewMode === "payment") {
                data?.forEach(group => {
                  group.parcels?.forEach(p => {
                    if (p.third_party_share > 0) {
                      pendingSettlements.push({
                        tracking: p.tracking,
                        date: p.date,
                        sale_amount: p.sale_amount,
                        third_party_share: p.third_party_share,
                        third_party_name: accountViewData.profit_sharing?.third_name || 'Third Party'
                      });
                    }
                  });
                });
              } else {
                data?.forEach(p => {
                  if (p.third_party_share > 0) {
                    pendingSettlements.push({
                      tracking: p.tracking,
                      date: p.date,
                      sale_amount: p.sale_amount,
                      third_party_share: p.third_party_share,
                      third_party_name: accountViewData.profit_sharing?.third_name || 'Third Party'
                    });
                  }
                });
              }
            }
            
            if (accountViewData?.third_partner_referrals?.length > 0) {
              accountViewData.third_partner_referrals.forEach(ref => {
                pendingSettlements.push({
                  tracking: ref.tracking,
                  date: ref.date,
                  sale_amount: ref.sale_amount,
                  third_party_share: ref.amount,
                  third_party_name: accountViewData.profit_sharing?.third_name || 'Third Party'
                });
              });
            }
            
            reports.settlement_pending = {
              title: 'Settlement Pending Report',
              items: pendingSettlements,
              count: pendingSettlements.length,
              third_party_name: accountViewData?.profit_sharing?.third_name || 'Third Party',
              total_pending: pendingSettlements.reduce((sum, p) => sum + (p.third_party_share || 0), 0),
              has_third_party: hasThirdParty || accountViewData?.third_partner_referrals?.length > 0
            };
            break;
            
          case 'month_end_summary':
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
            
            let monthlyDebits = 0, monthlyCredits = 0, monthlyParcels = 0;
            
            if (accountViewMode === "payment") {
              data?.forEach(group => {
                group.parcels?.forEach(p => {
                  const txnDate = p.date ? new Date(p.date) : null;
                  if (txnDate) {
                    const txnMonth = txnDate.getMonth();
                    const txnYear = txnDate.getFullYear();
                    
                    if (txnMonth === currentMonth && txnYear === currentYear) {
                      monthlyParcels++;
                      p.entries?.forEach(e => {
                        monthlyDebits += e.debit || 0;
                        monthlyCredits += e.credit || 0;
                      });
                    }
                  }
                });
              });
            } else {
              data?.forEach(p => {
                const txnDate = p.date ? new Date(p.date) : null;
                if (txnDate) {
                  const txnMonth = txnDate.getMonth();
                  const txnYear = txnDate.getFullYear();
                  
                  if (txnMonth === currentMonth && txnYear === currentYear) {
                    monthlyParcels++;
                    p.entries?.forEach(e => {
                      monthlyDebits += e.debit || 0;
                      monthlyCredits += e.credit || 0;
                    });
                  }
                }
              });
            }
            
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                               'July', 'August', 'September', 'October', 'November', 'December'];
            
            reports.month_end_summary = {
              title: 'Month-End Summary',
              current_month: monthNames[currentMonth],
              current_year: currentYear,
              opening_balance: accountViewData.opening_balance,
              monthly_debits: monthlyDebits,
              monthly_credits: monthlyCredits,
              closing_balance: accountViewData.closing_balance,
              parcels_count: monthlyParcels
            };
            break;
        }
      });
      
      setAccountReportData(reports);
      setShowAccountReportDialog(false);
      setShowFullScreenReport(true);
      toast.success(`Generated ${Object.keys(reports).length} reports - Opening full screen view`);
    } catch (error) {
      console.error("Report generation error:", error);
      toast.error("Failed to generate reports");
    } finally {
      setAccountReportLoading(false);
    }
  };

  // Print reports
  const printReports = () => {
    window.print();
  };

  // Export generated reports to Excel
  const exportReportsToExcel = () => {
    if (!accountReportData) return;
    
    const wb = XLSX.utils.book_new();
    
    if (accountReportData.outstanding_balance) {
      const data = [
        ['Outstanding Balance Report'],
        ['Partner', clientCode],
        [''],
        ['Metric', 'Value'],
        ['Total Debit', accountReportData.outstanding_balance.total_debit],
        ['Total Credit', accountReportData.outstanding_balance.total_credit],
        ['Net Balance', accountReportData.outstanding_balance.balance],
        ['Status', accountReportData.outstanding_balance.status]
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Outstanding Balance');
    }
    
    if (accountReportData.profit_summary) {
      const data = [
        ['Profit Share Summary'],
        ['Partner', clientCode],
        [''],
        ['Category', 'Amount'],
        [`Self Share (${accountViewData?.profit_sharing?.self_percentage || 50}%)`, accountReportData.profit_summary.self_share],
        [`Partner Share (${accountViewData?.profit_sharing?.partner_percentage || 50}%)`, accountReportData.profit_summary.partner_share],
        ['Total GP', accountReportData.profit_summary.total_gp]
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Profit Summary');
    }
    
    if (accountReportData.company_payments && accountReportData.company_payments.items?.length > 0) {
      const data = [
        ['Company Payments Report'],
        ['Total', accountReportData.company_payments.total || ''],
        ['Count', accountReportData.company_payments.count],
        [''],
        ['Payment Mode / Tracking', 'Amount', 'Parcel Count']
      ];
      accountReportData.company_payments.items.forEach(item => {
        data.push([
          item.payment_mode || item.tracking,
          item.payment_amount || item.sale_amount,
          item.parcel_count || 1
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Company Payments');
    }
    
    if (accountReportData.negative_gp && accountReportData.negative_gp.items?.length > 0) {
      const data = [
        ['Negative GP Report'],
        ['Count', accountReportData.negative_gp.count],
        [''],
        ['Tracking', 'GP']
      ];
      accountReportData.negative_gp.items.forEach(item => {
        data.push([item.tracking, item.gp_to_partner || item.gp]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Negative GP');
    }
    
    if (accountReportData.month_end_summary) {
      const data = [
        ['Month-End Summary'],
        ['Partner', clientCode],
        ['Period', `${accountReportData.month_end_summary.current_month} ${accountReportData.month_end_summary.current_year}`],
        [''],
        ['Opening Balance', accountReportData.month_end_summary.opening_balance],
        ['Monthly Debits', accountReportData.month_end_summary.monthly_debits],
        ['Monthly Credits', accountReportData.month_end_summary.monthly_credits],
        ['Closing Balance', accountReportData.month_end_summary.closing_balance],
        ['Parcels This Month', accountReportData.month_end_summary.parcels_count]
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'Month End Summary');
    }
    
    XLSX.writeFile(wb, `Reports_${clientCode}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Reports exported to Excel');
  };

  // Open manual entry dialog
  const openManualEntryDialog = (type) => {
    setManualEntryType(type);
    setManualEntryForm({ amount: '', description: '', date: '', category: 'adjustment' });
    setShowManualEntryDialog(true);
  };

  // Submit manual entry
  const handleManualEntrySubmit = async () => {
    if (!clientCode) {
      toast.error("Partner code not available");
      return;
    }
    if (!manualEntryForm.amount || parseFloat(manualEntryForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!manualEntryForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    if (!manualEntryForm.date) {
      toast.error("Please select a date");
      return;
    }

    setManualEntryLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Map debit/credit to take/give for existing API
      const apiEntryType = manualEntryType === 'debit' ? 'take' : 'give';
      
      const response = await fetch(`${API_URL}/api/ledger-reconciliation/manual-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          client_code: clientCode,
          entry_type: apiEntryType,
          amount: parseFloat(manualEntryForm.amount),
          narration: `[${manualEntryForm.category.toUpperCase()}] ${manualEntryForm.description}`,
          date: manualEntryForm.date
        })
      });

      if (response.ok) {
        toast.success(`${manualEntryType === 'debit' ? 'Debit' : 'Credit'} entry added successfully`);
        setShowManualEntryDialog(false);
        setManualEntryForm({ amount: '', description: '', date: '', category: 'adjustment' });
        // Refresh the account data
        fetchAccountData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add entry");
      }
    } catch (error) {
      console.error("Manual entry error:", error);
      toast.error("Failed to add entry");
    } finally {
      setManualEntryLoading(false);
    }
  };

  if (!clientCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">No Partner Selected</h1>
          <p className="text-gray-600 mb-4">Please access this page from the Account View.</p>
          <Button onClick={() => window.close()}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading ledger data for {clientCode}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Sticky Action Bar */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-3 print:hidden sticky top-0 z-50 shadow-sm">
        <div className="flex items-center justify-between max-w-[1800px] mx-auto">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-bold">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              PLA - {clientCode}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {accountViewMode === "payment" ? "Payment-wise" : "Parcel-wise"} | 
              {accountViewMode === "payment" 
                ? ` ${accountViewData?.payment_groups?.length || 0} groups`
                : ` ${accountViewData?.ledger_entries?.length || 0} entries`}
              {showFullDetails && " | Full Details"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mr-2">
              <Button
                size="sm"
                variant={accountViewMode === 'parcel' ? 'default' : 'ghost'}
                onClick={() => setAccountViewMode('parcel')}
                className={accountViewMode === 'parcel' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                Parcel-wise
              </Button>
              <Button
                size="sm"
                variant={accountViewMode === 'payment' ? 'default' : 'ghost'}
                onClick={() => setAccountViewMode('payment')}
                className={accountViewMode === 'payment' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              >
                Payment-wise
              </Button>
            </div>
            
            {/* Column Selector Dropdown */}
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="column-selector-btn"
              >
                <Filter className="w-4 h-4 mr-1" />
                Columns ({Object.values(exportColumns).filter(v => v).length - (accountViewMode !== 'payment' && exportColumns.grp_bal ? 1 : 0)})
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
              {showColumnSelector && (
                <div 
                  className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-3 min-w-[200px]"
                  data-testid="column-selector-dropdown"
                >
                  <p className="text-xs font-semibold text-slate-500 mb-2">Select Export Columns</p>
                  <div className="space-y-1.5">
                    {[
                      { key: 'sno', label: 'S.No' },
                      { key: 'date', label: 'Date' },
                      { key: 'tracking', label: 'Tracking' },
                      { key: 'sale', label: 'Sale' },
                      { key: 'gp', label: 'GP' },
                      { key: 'coll', label: 'Collection' },
                      { key: 'debit', label: 'Debit' },
                      { key: 'credit', label: 'Credit' },
                      { key: 'balance', label: 'Balance' },
                      ...(accountViewMode === 'payment' ? [{ key: 'grp_bal', label: 'Grp Bal' }] : [])
                    ].map(col => (
                      <label 
                        key={col.key} 
                        className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded"
                      >
                        <Checkbox
                          checked={exportColumns[col.key]}
                          onCheckedChange={(checked) => setExportColumns(prev => ({ ...prev, [col.key]: checked }))}
                          data-testid={`col-checkbox-${col.key}`}
                        />
                        <span className="text-sm text-slate-700">{col.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-100 flex gap-2">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-xs flex-1"
                      onClick={() => setExportColumns({
                        sno: true, date: true, tracking: true, sale: true, gp: true,
                        coll: true, debit: true, credit: true, balance: true, grp_bal: true
                      })}
                    >
                      Select All
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-xs flex-1"
                      onClick={() => setExportColumns({
                        sno: false, date: false, tracking: false, sale: false, gp: false,
                        coll: false, debit: false, credit: false, balance: false, grp_bal: false
                      })}
                    >
                      Deselect
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Full Details Toggle */}
            <Button 
              variant={showFullDetails ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFullDetails(!showFullDetails)}
              className={showFullDetails ? "bg-purple-600 hover:bg-purple-700 text-white" : "border-purple-300 text-purple-700 hover:bg-purple-50"}
            >
              <List className="w-4 h-4 mr-1" />
              {showFullDetails ? "Full Details ON" : "Full Details"}
            </Button>
            
            {/* Generate Report Button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowAccountReportDialog(true)}
              disabled={!accountViewData}
              className="border-purple-200 text-purple-700 hover:bg-purple-50"
              data-testid="generate-report-btn"
            >
              <BarChart3 className="w-4 h-4 mr-1" />
              Generate Report
            </Button>
            
            {/* Manual Debit/Credit Buttons */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => openManualEntryDialog('debit')}
              disabled={!accountViewData}
              className="border-red-200 text-red-700 hover:bg-red-50"
              data-testid="add-debit-btn"
            >
              <Minus className="w-4 h-4 mr-1" />
              Debit
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => openManualEntryDialog('credit')}
              disabled={!accountViewData}
              className="border-green-200 text-green-700 hover:bg-green-50"
              data-testid="add-credit-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Credit
            </Button>
            
            <div className="w-px h-6 bg-slate-300 mx-1"></div>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.print()}
              className="border-slate-300 hover:bg-slate-50"
            >
              <Printer className="w-4 h-4 mr-1" />
              Print
            </Button>
            <Button 
              size="sm"
              onClick={() => handleExport('excel')}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
              Excel
            </Button>
            <Button 
              size="sm"
              onClick={() => handleExport('pdf')}
              disabled={exporting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
              PDF
            </Button>
          </div>
        </div>
      </div>
      
      {/* Full Spreadsheet View */}
      <div className="flex-1 overflow-auto bg-white" id="ledger-print-content">
        {accountViewData && (
          <div className="min-w-[1100px] max-w-[1800px] mx-auto">
            {/* Document Header */}
            <div className="bg-gray-50 border-b border-slate-300 px-6 py-3 print:py-4">
              <div className="text-center">
                <h1 className="text-lg font-bold text-slate-800">PLA - {clientCode}</h1>
                <p className="text-xs text-slate-500 mt-1">
                  {accountViewMode === "payment" ? "Payment-wise" : "Parcel-wise"}
                  {dateFrom && ` | ${dateFrom}`}
                  {dateTo && ` to ${dateTo}`}
                  {` | Opening: ₹${(accountViewData.opening_balance || 0).toLocaleString()}`}
                  {` | Closing: ₹${accountViewData.closing_balance?.toLocaleString()}`}
                </p>
              </div>
            </div>
            
            {/* Excel-style Table */}
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-200 sticky top-0 print:bg-gray-200">
                <tr>
                  <th className="p-2 text-left border border-gray-300 w-10 font-semibold text-gray-700">S.No</th>
                  <th className="p-2 text-left border border-gray-300 w-20 font-semibold text-gray-700">Date</th>
                  <th className="p-2 text-left border border-gray-300 font-semibold text-gray-700">Tracking</th>
                  <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-gray-600">Sale</th>
                  <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-gray-600">GP</th>
                  <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-gray-600">Coll</th>
                  <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-gray-700">Debit</th>
                  <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-gray-700">Credit</th>
                  <th className="p-2 text-right border border-gray-300 w-28 font-semibold text-gray-700">Balance</th>
                  {accountViewMode === "payment" && (
                    <th className="p-2 text-right border border-gray-300 w-24 font-semibold text-orange-700">Grp Bal</th>
                  )}
                </tr>
                {/* TOTALS Summary Row */}
                <tr className="bg-gray-300 font-bold text-sm">
                  <td className="p-2 border border-gray-400 text-center">-</td>
                  <td className="p-2 border border-gray-400 font-bold">TOTALS</td>
                  <td className="p-2 border border-gray-400">-</td>
                  <td className="p-2 border border-gray-400 text-right text-blue-700">
                    {(() => {
                      if (accountViewMode === 'parcel') {
                        return (accountViewData.ledger_entries?.reduce((sum, e) => sum + (e.sale_amount || 0), 0) || 0).toLocaleString();
                      } else {
                        let total = 0;
                        accountViewData.payment_groups?.forEach(g => {
                          g.parcels?.forEach(p => { total += p.sale_amount || 0; });
                        });
                        return total.toLocaleString();
                      }
                    })()}
                  </td>
                  <td className="p-2 border border-gray-400 text-right text-blue-700">
                    {(() => {
                      if (accountViewMode === 'parcel') {
                        return (accountViewData.ledger_entries?.reduce((sum, e) => sum + (e.gp_to_partner || 0), 0) || 0).toLocaleString();
                      } else {
                        let total = 0;
                        accountViewData.payment_groups?.forEach(g => {
                          g.parcels?.forEach(p => { total += p.gp_to_partner || 0; });
                        });
                        return total.toLocaleString();
                      }
                    })()}
                  </td>
                  <td className="p-2 border border-gray-400 text-right text-purple-700 italic">
                    {(() => {
                      let total = 0;
                      if (accountViewMode === 'parcel') {
                        accountViewData.ledger_entries?.forEach(e => {
                          const personalEntry = e.entries?.find(en => en.category === 'personal');
                          total += personalEntry?.partner_collection || 0;
                        });
                      } else {
                        accountViewData.payment_groups?.forEach(g => {
                          if (!g.is_company_payment && g.payment_amount > 0) {
                            total += g.payment_amount;
                          }
                        });
                      }
                      return total.toLocaleString();
                    })()}
                  </td>
                  <td className="p-2 border border-gray-400 text-right text-red-700">
                    {(() => {
                      if (accountViewMode === 'parcel') {
                        return (accountViewData.ledger_entries?.reduce((sum, e) => sum + (e.parcel_summary?.total_debit || 0), 0) || 0).toLocaleString();
                      } else {
                        let total = 0;
                        accountViewData.payment_groups?.forEach(g => {
                          g.parcels?.forEach(p => { total += p.parcel_summary?.total_debit || 0; });
                        });
                        return total.toLocaleString();
                      }
                    })()}
                  </td>
                  <td className="p-2 border border-gray-400 text-right text-green-700">
                    {(() => {
                      if (accountViewMode === 'parcel') {
                        return (accountViewData.ledger_entries?.reduce((sum, e) => sum + (e.parcel_summary?.total_credit || 0), 0) || 0).toLocaleString();
                      } else {
                        // FIX: Use group_summary.total_credit (only company payments) instead of payment_amount (all payments)
                        let total = 0;
                        accountViewData.payment_groups?.forEach(g => {
                          total += g.group_summary?.total_credit || 0;
                        });
                        return total.toLocaleString();
                      }
                    })()}
                  </td>
                  <td className="p-2 border border-gray-400 text-right font-bold text-slate-800">
                    {accountViewData.closing_balance?.toLocaleString()}
                  </td>
                  {accountViewMode === "payment" && (
                    <td className="p-2 border border-gray-400 text-right font-bold">
                      {(() => {
                        let totalGrpBal = 0;
                        accountViewData.payment_groups?.forEach(g => {
                          if (g.parcel_count >= 2 && g.payment_amount > 0) {
                            const groupSales = g.parcels?.reduce((sum, p) => sum + (p.sale_amount || 0), 0) || 0;
                            totalGrpBal += groupSales - g.payment_amount;
                          }
                        });
                        const isPositive = totalGrpBal > 0;
                        const isNegative = totalGrpBal < 0;
                        return (
                          <span className={isPositive ? 'text-red-600' : isNegative ? 'text-green-600' : 'text-gray-500'}>
                            {totalGrpBal !== 0 ? totalGrpBal.toLocaleString() : '-'}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                </tr>
              </thead>
              <tbody>
                {/* Opening Balance Row */}
                <tr className="bg-gray-100 font-semibold">
                  <td className="p-2 border border-gray-200 text-center">-</td>
                  <td className="p-2 border border-gray-200">-</td>
                  <td className="p-2 border border-gray-200 font-bold">OPENING</td>
                  <td className="p-2 border border-gray-200 text-right text-gray-400">-</td>
                  <td className="p-2 border border-gray-200 text-right text-gray-400">-</td>
                  <td className="p-2 border border-gray-200 text-right text-gray-400">-</td>
                  <td className="p-2 border border-gray-200 text-right">-</td>
                  <td className="p-2 border border-gray-200 text-right">-</td>
                  <td className="p-2 border border-gray-200 text-right font-bold">
                    {(accountViewData.opening_balance || 0).toLocaleString()}
                  </td>
                  {accountViewMode === "payment" && (
                    <td className="p-2 border border-gray-200 text-right text-gray-400">-</td>
                  )}
                </tr>
                
                {/* Parcel-wise View */}
                {accountViewMode === "parcel" && accountViewData.ledger_entries?.map((entry, idx) => {
                  const hasNoInvoice = entry.has_no_invoice;
                  const hasNegGp = entry.has_negative_gp;
                  
                  const subEntries = entry.entries || [];
                  const costingEntry = subEntries.find(e => e.category === 'costing');
                  const gpSelfEntry = subEntries.find(e => e.category === 'gp_self');
                  const paymentEntry = subEntries.find(e => e.category && e.category.includes('payment'));
                  const personalEntry = subEntries.find(e => e.category === 'personal');
                  const lastEntry = subEntries[subEntries.length - 1];
                  
                  const costAmount = costingEntry?.debit || 0;
                  const gpSelf = gpSelfEntry?.debit || 0;
                  const saleAmount = entry.sale_amount || 0;
                  const gpPartner = entry.gp_to_partner || 0;
                  const creditAmount = paymentEntry?.credit || entry.parcel_summary?.total_credit || 0;
                  const personalCollectionAmount = personalEntry?.partner_collection || 0;
                  const totalDebit = entry.parcel_summary?.total_debit || (costAmount + gpSelf);
                  
                  const finalBalance = lastEntry?.balance ?? 0;
                  const paymentZero = !creditAmount || creditAmount === 0;
                  
                  const balanceAfterCosting = costingEntry?.balance ?? finalBalance;
                  const balanceAfterGpSelf = gpSelfEntry?.balance ?? balanceAfterCosting;
                  
                  let parcelHeaderBg = 'bg-blue-50/50';
                  if (hasNoInvoice) {
                    parcelHeaderBg = 'bg-amber-50';
                  } else if (paymentZero) {
                    parcelHeaderBg = 'bg-red-50/30';
                  }
                  
                  if (!showFullDetails) {
                    // COMPACT VIEW
                    return (
                      <tr key={`entry-${idx}`} className={parcelHeaderBg}>
                        <td className="p-2 border border-gray-200 text-center">{idx + 1}</td>
                        <td className="p-2 border border-gray-200 text-xs">{formatDMY(entry.date)}</td>
                        <td className="p-2 border border-gray-200">
                          <span className={`font-mono ${hasNoInvoice ? 'font-bold' : ''}`}>{entry.tracking}</span>
                          {hasNegGp && <span className="ml-2 text-xs text-red-600 font-bold">[NG]</span>}
                          {hasNoInvoice && <span className="ml-2 text-xs text-amber-600 font-bold">[NI]</span>}
                          {paymentZero && !hasNoInvoice && <span className="ml-2 text-xs text-red-500 font-bold">[P]</span>}
                        </td>
                        <td className="p-2 border border-gray-200 text-right text-gray-500">
                          {saleAmount ? saleAmount.toLocaleString() : '-'}
                        </td>
                        <td className="p-2 border border-gray-200 text-right text-gray-500">
                          {gpPartner ? gpPartner.toLocaleString() : '-'}
                        </td>
                        <td className={`p-2 border border-gray-200 text-right ${personalCollectionAmount > 0 ? 'text-purple-600 font-medium italic' : 'text-gray-400'}`}>
                          {personalCollectionAmount > 0 ? personalCollectionAmount.toLocaleString() : '-'}
                        </td>
                        <td className="p-2 border border-gray-200 text-right font-medium">
                          {totalDebit > 0 ? totalDebit.toLocaleString() : '-'}
                        </td>
                        <td className={`p-2 border border-gray-200 text-right font-medium ${creditAmount > 0 ? 'bg-green-50' : ''}`}>
                          {creditAmount > 0 ? creditAmount.toLocaleString() : '-'}
                        </td>
                        <td className={`p-2 border border-gray-200 text-right font-bold ${
                          finalBalance >= 0 ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {finalBalance?.toLocaleString()}
                        </td>
                      </tr>
                    );
                  }
                  
                  // FULL DETAILS VIEW
                  return (
                    <React.Fragment key={`entry-${idx}`}>
                      <tr className={`${parcelHeaderBg} font-semibold`}>
                        <td className="p-2 border border-gray-200 text-center">{idx + 1}</td>
                        <td colSpan={8} className="p-2 border border-gray-200">
                          <span className="font-bold">** PARCEL: </span>
                          <span className={`font-mono ${hasNoInvoice ? 'font-bold' : ''}`}>{entry.tracking}</span>
                          {hasNegGp && <span className="ml-2 text-xs text-red-600 font-bold">[NG]</span>}
                          {hasNoInvoice && <span className="ml-2 text-xs text-amber-600 font-bold">[NI]</span>}
                        </td>
                      </tr>
                      
                      {costAmount > 0 && (
                        <tr className="bg-white">
                          <td className="p-1 border border-gray-200"></td>
                          <td className="p-1 border border-gray-200 text-xs pl-4">{entry.date || ''}</td>
                          <td className="p-1 border border-gray-200 pl-8 text-gray-600">Costing</td>
                          <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                          <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                          <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                          <td className="p-1 border border-gray-200 text-right text-red-600">{costAmount.toLocaleString()}</td>
                          <td className="p-1 border border-gray-200 text-right">-</td>
                          <td className={`p-1 border border-gray-200 text-right ${balanceAfterCosting >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {balanceAfterCosting.toLocaleString()}
                          </td>
                        </tr>
                      )}
                      
                      {gpSelf > 0 && (
                        <tr className="bg-white">
                          <td className="p-1 border border-gray-200"></td>
                          <td className="p-1 border border-gray-200 text-xs pl-4">{entry.date || ''}</td>
                          <td className="p-1 border border-gray-200 pl-8 text-gray-600">
                            GP Self ({accountViewData.profit_sharing?.self_percentage || 50}%)
                          </td>
                          <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                          <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                          <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                          <td className="p-1 border border-gray-200 text-right text-red-600">{gpSelf.toLocaleString()}</td>
                          <td className="p-1 border border-gray-200 text-right">-</td>
                          <td className={`p-1 border border-gray-200 text-right ${balanceAfterGpSelf >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {balanceAfterGpSelf.toLocaleString()}
                          </td>
                        </tr>
                      )}
                      
                      <tr className="bg-gray-50/50">
                        <td className="p-1 border border-gray-200"></td>
                        <td className="p-1 border border-gray-200 text-xs pl-4">{entry.date || ''}</td>
                        <td className="p-1 border border-gray-200 pl-8 text-gray-600">Sale Invoice</td>
                        <td className="p-1 border border-gray-200 text-right text-gray-500">{saleAmount ? saleAmount.toLocaleString() : '-'}</td>
                        <td className="p-1 border border-gray-200 text-right text-gray-500">{gpPartner ? gpPartner.toLocaleString() : '-'}</td>
                        <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                        <td className="p-1 border border-gray-200 text-right">-</td>
                        <td className="p-1 border border-gray-200 text-right">-</td>
                        <td className={`p-1 border border-gray-200 text-right ${balanceAfterGpSelf >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {(gpSelf > 0 ? balanceAfterGpSelf : balanceAfterCosting).toLocaleString()}
                        </td>
                      </tr>
                      
                      {creditAmount > 0 && (
                        <tr className="bg-green-50/50">
                          <td className="p-1 border border-gray-200"></td>
                          <td className="p-1 border border-gray-200 text-xs pl-4">{entry.date || ''}</td>
                          <td className="p-1 border border-gray-200 pl-8 text-green-700 font-medium">
                            Payment Received ({paymentEntry?.payment_mode || 'ESR'})
                          </td>
                          <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                          <td className="p-1 border border-gray-200 text-right text-gray-400">-</td>
                          <td className={`p-1 border border-gray-200 text-right ${personalCollectionAmount > 0 ? 'text-purple-600 font-medium italic' : 'text-gray-400'}`}>
                            {personalCollectionAmount > 0 ? personalCollectionAmount.toLocaleString() : '-'}
                          </td>
                          <td className="p-1 border border-gray-200 text-right">-</td>
                          <td className="p-1 border border-gray-200 text-right text-green-700 font-medium">
                            {creditAmount.toLocaleString()}
                          </td>
                          <td className={`p-1 border border-gray-200 text-right font-bold ${finalBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {finalBalance.toLocaleString()}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                
                {/* Payment-wise View */}
                {accountViewMode === "payment" && accountViewData.payment_groups?.map((group, groupIdx) => {
                  const isCompanyPayment = group.is_company_payment;
                  const paymentAmount = group.payment_amount || 0;
                  const parcelCount = group.parcel_count || 0;
                  const paymentMode = group.payment_mode || 'Unknown';
                  
                  const groupBgClass = isCompanyPayment 
                    ? 'bg-emerald-50' 
                    : paymentAmount === 0 
                      ? 'bg-red-50/50' 
                      : 'bg-purple-50';
                  
                  // Group totals for multi-parcel
                  const groupTotalSale = group.group_summary?.total_sale || 0;
                  const groupTotalGp = group.group_summary?.total_gp || 0;
                  const groupBalance = parcelCount >= 2 && paymentAmount > 0 ? (groupTotalSale - paymentAmount) : null;
                  
                  return (
                    <React.Fragment key={`group-${groupIdx}`}>
                      {/* Payment Group Header */}
                      <tr className={`${groupBgClass} font-semibold border-t-2 border-gray-400`}>
                        <td className="p-2 border border-gray-200 text-center">{groupIdx + 1}</td>
                        <td className="p-2 border border-gray-200 text-xs">{formatDMY(group.date)}</td>
                        <td className="p-2 border border-gray-200">
                          <span className="font-bold">PMT: </span>
                          {isCompanyPayment ? (
                            <span className="text-emerald-700">{paymentMode} {parcelCount > 1 ? `(${parcelCount})` : ''}</span>
                          ) : paymentAmount === 0 ? (
                            <span className="text-red-600">No Payment ({parcelCount} parcels)</span>
                          ) : (
                            <span className="text-purple-700">Personal {paymentMode} {parcelCount > 1 ? `(${parcelCount})` : ''}</span>
                          )}
                        </td>
                        <td className="p-2 border border-gray-200 text-right text-blue-600">
                          {groupTotalSale > 0 ? groupTotalSale.toLocaleString() : '-'}
                        </td>
                        <td className="p-2 border border-gray-200 text-right text-blue-600">
                          {groupTotalGp > 0 ? groupTotalGp.toLocaleString() : '-'}
                        </td>
                        <td className={`p-2 border border-gray-200 text-right ${!isCompanyPayment && paymentAmount > 0 ? 'text-purple-600 font-bold italic' : 'text-gray-400'}`}>
                          {!isCompanyPayment && paymentAmount > 0 ? paymentAmount.toLocaleString() : '-'}
                        </td>
                        <td className="p-2 border border-gray-200 text-right text-gray-400">-</td>
                        <td className={`p-2 border border-gray-200 text-right ${isCompanyPayment ? 'text-green-700 font-bold' : 'text-gray-400'}`}>
                          {isCompanyPayment ? paymentAmount.toLocaleString() : '-'}
                        </td>
                        <td className="p-2 border border-gray-200 text-right font-bold text-gray-400">
                          -
                        </td>
                        <td className={`p-2 border border-gray-200 text-right font-bold ${
                          groupBalance !== null 
                            ? (groupBalance > 0 ? 'text-red-600' : groupBalance < 0 ? 'text-green-600' : 'text-gray-500')
                            : 'text-gray-400'
                        }`}>
                          {groupBalance !== null ? groupBalance.toLocaleString() : '-'}
                        </td>
                      </tr>
                      
                      {/* Individual Parcels in Group */}
                      {showFullDetails && group.parcels?.map((parcel, parcelIdx) => {
                        const pSaleAmount = parcel.sale_amount || 0;
                        const pGpPartner = parcel.gp_to_partner || 0;
                        const pDebit = parcel.parcel_summary?.total_debit || 0;
                        const pCredit = parcel.is_last_in_group ? (isCompanyPayment ? paymentAmount : 0) : 0;
                        const pBalance = parcel.running_balance ?? 0;
                        
                        return (
                          <tr key={`parcel-${groupIdx}-${parcelIdx}`} className="bg-white hover:bg-gray-50">
                            <td className="p-1 border border-gray-200 text-center text-gray-400 text-xs">
                              {groupIdx + 1}.{parcelIdx + 1}
                            </td>
                            <td className="p-1 border border-gray-200 text-xs text-gray-500 pl-4">
                              {formatDMY(parcel.date)}
                            </td>
                            <td className="p-1 border border-gray-200 pl-6">
                              <span className="font-mono text-xs">{parcel.tracking}</span>
                              {parcel.has_negative_gp && <span className="ml-1 text-xs text-red-600">[NG]</span>}
                              {parcel.has_no_invoice && <span className="ml-1 text-xs text-amber-600">[NI]</span>}
                            </td>
                            <td className="p-1 border border-gray-200 text-right text-gray-500 text-xs">
                              {pSaleAmount > 0 ? pSaleAmount.toLocaleString() : '-'}
                            </td>
                            <td className="p-1 border border-gray-200 text-right text-gray-500 text-xs">
                              {pGpPartner > 0 ? pGpPartner.toLocaleString() : '-'}
                            </td>
                            <td className="p-1 border border-gray-200 text-right text-gray-400 text-xs">-</td>
                            <td className="p-1 border border-gray-200 text-right text-red-600 text-xs">
                              {pDebit > 0 ? pDebit.toLocaleString() : '-'}
                            </td>
                            <td className={`p-1 border border-gray-200 text-right text-xs ${pCredit > 0 ? 'text-green-700 font-medium bg-green-50' : 'text-gray-400'}`}>
                              {pCredit > 0 ? pCredit.toLocaleString() : '-'}
                            </td>
                            <td className={`p-1 border border-gray-200 text-right text-xs font-medium ${pBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {pBalance.toLocaleString()}
                            </td>
                            <td className="p-1 border border-gray-200 text-right text-gray-400 text-xs">-</td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                
                {/* Closing Balance Row */}
                <tr className="bg-gray-200 font-bold border-t-2 border-gray-400">
                  <td className="p-2 border border-gray-300 text-center">-</td>
                  <td className="p-2 border border-gray-300">-</td>
                  <td className="p-2 border border-gray-300 font-bold">CLOSING BALANCE</td>
                  <td className="p-2 border border-gray-300 text-right text-gray-400">-</td>
                  <td className="p-2 border border-gray-300 text-right text-gray-400">-</td>
                  <td className="p-2 border border-gray-300 text-right text-gray-400">-</td>
                  <td className="p-2 border border-gray-300 text-right">-</td>
                  <td className="p-2 border border-gray-300 text-right">-</td>
                  <td className={`p-2 border border-gray-300 text-right font-bold text-lg ${
                    (accountViewData.closing_balance || 0) >= 0 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {accountViewData.closing_balance?.toLocaleString()}
                  </td>
                  {accountViewMode === "payment" && (
                    <td className="p-2 border border-gray-300 text-right text-gray-400">-</td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { margin: 0.5cm; size: landscape; }
        }
      `}</style>

      {/* Report Selection Dialog */}
      <Dialog open={showAccountReportDialog} onOpenChange={setShowAccountReportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Generate Account Reports
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Select reports to generate for {clientCode} ({accountViewMode === "payment" ? "Payment-wise" : "Parcel-wise"} view)
            </p>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Report Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {accountReportTypes.map(report => {
                const count = getAccountReportCount(report.id);
                const isSelected = selectedAccountReports.includes(report.id);
                return (
                  <div
                    key={report.id}
                    onClick={() => toggleAccountReportSelection(report.id)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-slate-200 hover:border-purple-300 hover:bg-slate-50'
                    }`}
                    data-testid={`report-type-${report.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{report.name}</span>
                      <Badge className={`bg-${report.color}-100 text-${report.color}-700 text-xs`}>
                        {count}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500">{report.description}</p>
                  </div>
                );
              })}
            </div>
            
            {/* Generate Button */}
            <div className="flex justify-between items-center pt-4 border-t">
              <span className="text-sm text-slate-500">
                {selectedAccountReports.length} report(s) selected
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedAccountReports([])}
                >
                  Clear All
                </Button>
                <Button 
                  onClick={generateAccountReports}
                  disabled={selectedAccountReports.length === 0 || accountReportLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="generate-reports-submit-btn"
                >
                  {accountReportLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <BarChart3 className="w-4 h-4 mr-2" />
                  )}
                  Generate Reports
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Screen Report View Dialog */}
      <Dialog open={showFullScreenReport} onOpenChange={setShowFullScreenReport}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full overflow-hidden flex flex-col">
          {/* Sticky Action Bar */}
          <div className="flex-shrink-0 bg-white border-b border-slate-200 p-4 print:hidden">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                  Account Reports - {clientCode}
                </DialogTitle>
                <p className="text-sm text-slate-500 mt-1">
                  {accountViewMode === "payment" ? "Payment-wise" : "Parcel-wise"} View | 
                  Generated: {new Date().toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button 
                  variant="outline" 
                  onClick={printReports}
                  className="border-slate-300 hover:bg-slate-50"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button 
                  onClick={exportReportsToExcel}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowFullScreenReport(false)}
                  className="hover:bg-slate-100"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Scrollable Report Content */}
          <div className="flex-1 overflow-y-auto p-6 print:p-0" id="print-report-content">
            {/* Print Header */}
            <div className="hidden print:block mb-6">
              <h1 className="text-2xl font-bold text-center">Account Reports - {clientCode}</h1>
              <p className="text-center text-slate-500">
                {accountViewMode === "payment" ? "Payment-wise" : "Parcel-wise"} View | 
                Generated: {new Date().toLocaleString()}
              </p>
            </div>
            
            {accountReportData && (
              <div className="space-y-6 print:space-y-4">
                {/* Outstanding Balance */}
                {accountReportData.outstanding_balance && (
                  <div className="p-6 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border print:border-2 print:p-4">
                    <h3 className="text-xl font-bold text-slate-800 mb-4 print:text-lg">📊 Outstanding Balance Report</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 print:gap-4">
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Total Debit</p>
                        <p className="text-2xl font-bold text-red-600 print:text-xl">₹{accountReportData.outstanding_balance.total_debit?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Total Credit</p>
                        <p className="text-2xl font-bold text-green-600 print:text-xl">₹{accountReportData.outstanding_balance.total_credit?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Net Balance</p>
                        <p className={`text-2xl font-bold print:text-xl ${accountReportData.outstanding_balance.balance > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ₹{accountReportData.outstanding_balance.balance?.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Status</p>
                        <p className={`text-lg font-bold ${accountReportData.outstanding_balance.balance > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {accountReportData.outstanding_balance.status}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Profit Summary */}
                {accountReportData.profit_summary && (
                  <div className="p-6 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200 print:border-2 print:p-4">
                    <h3 className="text-xl font-bold text-emerald-800 mb-4 print:text-lg">💰 Profit Share Summary</h3>
                    <div className="grid grid-cols-3 gap-6 print:gap-4">
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Self Share ({accountViewData?.profit_sharing?.self_percentage || 50}%)</p>
                        <p className="text-2xl font-bold text-emerald-600 print:text-xl">₹{accountReportData.profit_summary.self_share?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Partner Share ({accountViewData?.profit_sharing?.partner_percentage || 50}%)</p>
                        <p className="text-2xl font-bold text-blue-600 print:text-xl">₹{accountReportData.profit_summary.partner_share?.toLocaleString()}</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm print:shadow-none print:border">
                        <p className="text-sm text-slate-500 mb-1">Total GP</p>
                        <p className="text-2xl font-bold text-slate-800 print:text-xl">₹{accountReportData.profit_summary.total_gp?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Company Payments */}
                {accountReportData.company_payments && accountReportData.company_payments.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-green-800 print:text-lg">🏦 Company Payments Report</h3>
                      <div className="flex items-center gap-4">
                        {accountReportData.company_payments.total && (
                          <span className="text-lg font-bold text-green-700">Total: ₹{accountReportData.company_payments.total?.toLocaleString()}</span>
                        )}
                        <Badge className="bg-green-100 text-green-700 text-sm px-3 py-1">{accountReportData.company_payments.count} payments</Badge>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-green-100">
                            <th className="text-left p-3 border border-green-200">#</th>
                            <th className="text-left p-3 border border-green-200">Payment Mode / Tracking</th>
                            <th className="text-right p-3 border border-green-200">Amount</th>
                            <th className="text-right p-3 border border-green-200">Parcels</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.company_payments.items?.map((item, idx) => (
                            <tr key={idx} className="hover:bg-green-50">
                              <td className="p-3 border border-green-100">{idx + 1}</td>
                              <td className="p-3 border border-green-100 font-mono">{item.payment_mode || item.tracking}</td>
                              <td className="p-3 border border-green-100 text-right font-semibold">₹{(item.payment_amount || item.sale_amount)?.toLocaleString()}</td>
                              <td className="p-3 border border-green-100 text-right">{item.parcel_count || 1}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Personal Collections */}
                {accountReportData.personal_collections && accountReportData.personal_collections.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg border border-yellow-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-yellow-800 print:text-lg">👤 Personal Collections (Neutral)</h3>
                      <div className="flex items-center gap-4">
                        {accountReportData.personal_collections.total && (
                          <span className="text-lg font-bold text-yellow-700">Total: ₹{accountReportData.personal_collections.total?.toLocaleString()}</span>
                        )}
                        <Badge className="bg-yellow-100 text-yellow-700 text-sm px-3 py-1">{accountReportData.personal_collections.count} collections</Badge>
                      </div>
                    </div>
                    <p className="text-sm text-yellow-600 mb-3">These payments have no effect on balance (neutral)</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-yellow-100">
                            <th className="text-left p-3 border border-yellow-200">#</th>
                            <th className="text-left p-3 border border-yellow-200">Payment Mode / Tracking</th>
                            <th className="text-right p-3 border border-yellow-200">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.personal_collections.items?.map((item, idx) => (
                            <tr key={idx} className="hover:bg-yellow-50">
                              <td className="p-3 border border-yellow-100">{idx + 1}</td>
                              <td className="p-3 border border-yellow-100 font-mono">{item.payment_mode || item.tracking}</td>
                              <td className="p-3 border border-yellow-100 text-right font-semibold">₹{(item.payment_amount || item.sale_amount)?.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Multi-Parcel Payments */}
                {accountReportData.multi_parcel && accountReportData.multi_parcel.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-purple-800 print:text-lg">📦 Multi-Parcel Payments</h3>
                      <Badge className="bg-purple-100 text-purple-700 text-sm px-3 py-1">{accountReportData.multi_parcel.count} payments</Badge>
                    </div>
                    {accountReportData.multi_parcel.note && (
                      <p className="text-sm text-purple-600 italic">{accountReportData.multi_parcel.note}</p>
                    )}
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {accountReportData.multi_parcel.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-2 rounded border">
                          <span className="text-purple-700">{item.payment_mode} ({item.parcel_count} parcels)</span>
                          <span className="font-semibold">₹{item.payment_amount?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Negative GP */}
                {accountReportData.negative_gp && accountReportData.negative_gp.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-red-50 to-rose-50 rounded-lg border border-red-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-red-800 print:text-lg">📉 Negative GP Report (Loss)</h3>
                      <Badge className="bg-red-100 text-red-700 text-sm px-3 py-1">{accountReportData.negative_gp.count} parcels</Badge>
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {accountReportData.negative_gp.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-2 rounded border">
                          <span className="font-mono text-red-700">{item.tracking}</span>
                          <span className="font-semibold text-red-600">GP: ₹{(item.gp_to_partner || item.gp)?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* No Invoice */}
                {accountReportData.no_invoice && accountReportData.no_invoice.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-orange-800 print:text-lg">📋 No Invoice Report</h3>
                      <Badge className="bg-orange-100 text-orange-700 text-sm px-3 py-1">{accountReportData.no_invoice.count} parcels</Badge>
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {accountReportData.no_invoice.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-2 rounded border">
                          <span className="font-mono text-orange-700">{item.tracking}</span>
                          <span className="text-slate-500">{formatDMY(item.date)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Pending Payment */}
                {accountReportData.pending_payment && accountReportData.pending_payment.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg border border-slate-300 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-slate-800 print:text-lg">⏳ Pending Payment Report</h3>
                      <div className="flex items-center gap-3">
                        {accountReportData.pending_payment.total_pending > 0 && (
                          <span className="text-lg font-bold text-orange-600">
                            Total: ₹{accountReportData.pending_payment.total_pending?.toLocaleString()}
                          </span>
                        )}
                        <Badge className="bg-slate-200 text-slate-700 text-sm px-3 py-1">
                          {accountReportData.pending_payment.count} parcels
                        </Badge>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left p-2 border border-slate-200 w-10">#</th>
                            <th className="text-left p-2 border border-slate-200">Tracking</th>
                            <th className="text-left p-2 border border-slate-200">Customer</th>
                            <th className="text-right p-2 border border-slate-200">Sale Amount</th>
                            <th className="text-left p-2 border border-slate-200">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountReportData.pending_payment.items?.map((item, idx) => (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="p-2 border border-slate-100 text-slate-500">{idx + 1}</td>
                              <td className="p-2 border border-slate-100 font-mono text-slate-700">
                                {item.tracking || '-'}
                              </td>
                              <td className="p-2 border border-slate-100 text-slate-600 max-w-[150px] truncate">
                                {item.customer || '-'}
                              </td>
                              <td className="p-2 border border-slate-100 text-right font-semibold text-orange-600">
                                ₹{(item.sale_amount || 0).toLocaleString()}
                              </td>
                              <td className="p-2 border border-slate-100 text-slate-500">
                                {formatDMY(item.date)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* High Value Transactions */}
                {accountReportData.high_value && accountReportData.high_value.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-blue-800 print:text-lg">💎 High Value Transactions (≥₹10K)</h3>
                      <Badge className="bg-blue-100 text-blue-700 text-sm px-3 py-1">{accountReportData.high_value.count} transactions</Badge>
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {accountReportData.high_value.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-2 rounded border">
                          <span className="font-mono text-blue-700">{item.tracking || item.payment_mode}</span>
                          <span className="font-semibold text-blue-800">₹{(item.payment_amount || item.sale_amount)?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Dormant Account */}
                {accountReportData.dormant_account && (
                  <div className={`p-6 rounded-lg border print:border-2 print:p-4 ${
                    accountReportData.dormant_account.is_dormant 
                      ? 'bg-gradient-to-r from-slate-100 to-gray-100 border-slate-400' 
                      : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
                  }`}>
                    <h3 className={`text-xl font-bold mb-4 flex items-center justify-between ${
                      accountReportData.dormant_account.is_dormant ? 'text-slate-700' : 'text-green-800'
                    }`}>
                      <span>💤 Dormant Account Report</span>
                      <Badge className={`${
                        accountReportData.dormant_account.is_dormant 
                          ? 'bg-slate-300 text-slate-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {accountReportData.dormant_account.is_dormant ? 'Dormant' : 'Active'}
                      </Badge>
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white px-4 py-3 rounded border">
                        <span className="text-slate-500">Days Since Last Txn:</span>
                        <span className={`ml-2 font-semibold ${
                          accountReportData.dormant_account.days_since_last_transaction > 30 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {accountReportData.dormant_account.days_since_last_transaction ?? 'N/A'}
                        </span>
                      </div>
                      <div className="bg-white px-4 py-3 rounded border">
                        <span className="text-slate-500">Last Txn:</span>
                        <span className="ml-2 font-semibold text-slate-700">
                          {accountReportData.dormant_account.last_transaction_date}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Dispute/Adjustment Report */}
                {accountReportData.dispute_adjustment && accountReportData.dispute_adjustment.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-rose-50 to-red-50 rounded-lg border border-rose-300 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-rose-800 print:text-lg">⚖️ Dispute/Adjustment Report</h3>
                      <Badge className="bg-rose-100 text-rose-700 text-sm px-3 py-1">{accountReportData.dispute_adjustment.count} items</Badge>
                    </div>
                    <p className="text-sm text-rose-600 mb-3">Total Adjustment: ₹{accountReportData.dispute_adjustment.total_adjustment?.toLocaleString()}</p>
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {accountReportData.dispute_adjustment.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-2 rounded border">
                          <span className="font-mono text-rose-700">{item.tracking}</span>
                          <span className={`font-semibold ${item.adjustment_amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            ₹{item.adjustment_amount?.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Settlement Pending */}
                {accountReportData.settlement_pending && accountReportData.settlement_pending.count > 0 && (
                  <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-300 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-indigo-800 print:text-lg">🤝 Settlement Pending</h3>
                      <Badge className="bg-indigo-100 text-indigo-700 text-sm px-3 py-1">{accountReportData.settlement_pending.count} items</Badge>
                    </div>
                    <p className="text-sm text-indigo-600 mb-3">
                      Pending with {accountReportData.settlement_pending.third_party_name}: ₹{accountReportData.settlement_pending.total_pending?.toLocaleString()}
                    </p>
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {accountReportData.settlement_pending.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white px-3 py-2 rounded border">
                          <span className="font-mono text-indigo-700">{item.tracking}</span>
                          <span className="font-semibold text-indigo-800">₹{(item.third_party_share || 0).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Month-End Summary */}
                {accountReportData.month_end_summary && (
                  <div className="p-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-300 print:border-2 print:p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-cyan-800 print:text-lg">📅 Month-End Summary</h3>
                      <Badge className="bg-cyan-100 text-cyan-700 text-sm px-3 py-1">
                        {accountReportData.month_end_summary.current_month} {accountReportData.month_end_summary.current_year}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="bg-white px-4 py-3 rounded border text-center">
                        <p className="text-xs text-slate-500">Opening</p>
                        <p className="font-semibold text-slate-700">₹{accountReportData.month_end_summary.opening_balance?.toLocaleString()}</p>
                      </div>
                      <div className="bg-white px-4 py-3 rounded border text-center">
                        <p className="text-xs text-slate-500">Debits</p>
                        <p className="font-semibold text-red-600">₹{accountReportData.month_end_summary.monthly_debits?.toLocaleString()}</p>
                      </div>
                      <div className="bg-white px-4 py-3 rounded border text-center">
                        <p className="text-xs text-slate-500">Credits</p>
                        <p className="font-semibold text-green-600">₹{accountReportData.month_end_summary.monthly_credits?.toLocaleString()}</p>
                      </div>
                      <div className="bg-white px-4 py-3 rounded border text-center">
                        <p className="text-xs text-slate-500">Closing</p>
                        <p className={`font-semibold ${accountReportData.month_end_summary.closing_balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          ₹{accountReportData.month_end_summary.closing_balance?.toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-white px-4 py-3 rounded border text-center">
                        <p className="text-xs text-slate-500">Parcels</p>
                        <p className="font-semibold text-slate-700">{accountReportData.month_end_summary.parcels_count}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Debit/Credit Entry Dialog */}
      <Dialog open={showManualEntryDialog} onOpenChange={setShowManualEntryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {manualEntryType === 'debit' ? (
                <>
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Minus className="w-5 h-5 text-red-600" />
                  </div>
                  <span>Add Debit Entry</span>
                </>
              ) : (
                <>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Plus className="w-5 h-5 text-green-600" />
                  </div>
                  <span>Add Credit Entry</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm text-slate-600">
                Partner: <span className="font-semibold text-slate-800">{clientCode}</span>
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="entry-amount">Amount (₹) *</Label>
              <Input
                id="entry-amount"
                type="number"
                placeholder="Enter amount"
                value={manualEntryForm.amount}
                onChange={(e) => setManualEntryForm(prev => ({ ...prev, amount: e.target.value }))}
                className="text-lg font-semibold"
                data-testid="entry-amount-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="entry-date">Date *</Label>
              <DMYDateInput
  id="entry-date" value={manualEntryForm.date}
                onChange={(e) => setManualEntryForm(prev => ({ ...prev, date: e.target.value }))}
                data-testid="entry-date-input"
              />
              <p className="text-xs text-slate-500">Select the date for this entry (can be past date)</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="entry-category">Category</Label>
              <Select 
                value={manualEntryForm.category} 
                onValueChange={(value) => setManualEntryForm(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger data-testid="entry-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="adjustment">Adjustment</SelectItem>
                  <SelectItem value="refund">Refund</SelectItem>
                  <SelectItem value="advance">Advance Payment</SelectItem>
                  <SelectItem value="correction">Correction</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="entry-description">Description / Particulars *</Label>
              <Input
                id="entry-description"
                placeholder="Enter description for this entry"
                value={manualEntryForm.description}
                onChange={(e) => setManualEntryForm(prev => ({ ...prev, description: e.target.value }))}
                data-testid="entry-description-input"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowManualEntryDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleManualEntrySubmit}
              disabled={manualEntryLoading}
              className={manualEntryType === 'debit' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
              data-testid="submit-entry-btn"
            >
              {manualEntryLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : manualEntryType === 'debit' ? (
                <Minus className="w-4 h-4 mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Add {manualEntryType === 'debit' ? 'Debit' : 'Credit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LedgerPreviewPage;
